import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { absencesApi } from '../../lib/api/absences.api';
import { employeesApi } from '../../lib/api/employees.api';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Loader from '../../components/common/Loader';

interface Employee {
    id: string;
    name: string;
    dni: string;
}

interface CompanyClosureResult {
    totalEmployees: number;
    successCount: number;
    errorCount: number;
    errors: any[];
}

const CierreEmpresaPage: React.FC = () => {
    useAuth();
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [showResults, setShowResults] = useState(false);
    const [results, setResults] = useState<CompanyClosureResult | null>(null);

    const [formData, setFormData] = useState({
        startDate: '',
        endDate: '',
        reason: '',
        employeeIds: [] as string[],
    });

    const [selectedEmployees, setSelectedEmployees] = useState<Employee[]>([]);
    const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
    const [selectAll, setSelectAll] = useState(false);

    // Cargar empleados al montar
    React.useEffect(() => {
        loadEmployees();
    }, []);

    const loadEmployees = async () => {
        try {
            const response = await employeesApi.getEmployees();
            setAllEmployees(response.employees);
            setSelectedEmployees(response.employees);
            setSelectAll(true);
        } catch (err) {
            console.error('Error al cargar empleados:', err);
            setError('Error al cargar empleados');
        }
    };

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setSuccess(null);
        setError(null);
    };

    const handleReasonChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData(prev => ({ ...prev, reason: e.target.value }));
        setSuccess(null);
        setError(null);
    };

    const handleSelectAll = (checked: boolean) => {
        setSelectAll(checked);
        setSelectedEmployees(checked ? [...allEmployees] : []);
    };

    const handleEmployeeToggle = (employeeId: string) => {
        setSelectedEmployees(prev => {
            if (prev.find(e => e.id === employeeId)) {
                return prev.filter(e => e.id !== employeeId);
            } else {
                return [...prev, allEmployees.find(e => e.id === employeeId)!];
            }
        });
        setSelectAll(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.startDate || !formData.endDate) {
            setError('Las fechas son obligatorias');
            return;
        }

        if (!formData.reason.trim()) {
            setError('El motivo es obligatorio');
            return;
        }

        if (selectedEmployees.length === 0) {
            setError('Debe seleccionar al menos un empleado');
            return;
        }

        try {
            setSubmitting(true);
            setError(null);
            setSuccess(null);
            setShowResults(false);

            const result = await absencesApi.createCompanyClosure({
                startDate: formData.startDate,
                endDate: formData.endDate,
                reason: formData.reason,
                employeeIds: selectedEmployees.map(e => e.id),
            });

            setResults(result);
            setShowResults(true);
            setSuccess(`Cierre de empresa creado: ${result.successCount} de ${result.totalEmployees} empleados asignados correctamente.`);

            // Limpiar formulario
            setFormData({
                startDate: '',
                endDate: '',
                reason: '',
                employeeIds: [],
            });
        } catch (err: any) {
            console.error('Error al crear cierre de empresa:', err);
            setError(err.message || 'Error al crear cierre de empresa');
        } finally {
            setSubmitting(false);
        }
    };

    const calculateDays = () => {
        if (!formData.startDate || !formData.endDate) return 0;
        const start = new Date(formData.startDate);
        const end = new Date(formData.endDate);
        const diffTime = Math.abs(end.getTime() - start.getTime());
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    };

    if (submitting) {
        return <Loader />;
    }

    return (
        <div className="max-w-6xl mx-auto">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
                Cierre de Empresa
            </h1>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    Crear Cierre de Empresa
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Fechas */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Fecha de Inicio *
                            </label>
                            <Input
                                type="date"
                                name="startDate"
                                value={formData.startDate}
                                onChange={handleDateChange}
                                className="w-full"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Fecha de Fin *
                            </label>
                            <Input
                                type="date"
                                name="endDate"
                                value={formData.endDate}
                                onChange={handleDateChange}
                                className="w-full"
                            />
                        </div>
                    </div>

                    {/* Días calculados */}
                    {formData.startDate && formData.endDate && (
                        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                            <p className="text-sm text-blue-800 dark:text-blue-200">
                                <span className="font-semibold">{calculateDays()} días</span> de cierre de empresa
                            </p>
                        </div>
                    )}

                    {/* Motivo */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Motivo del Cierre *
                        </label>
                        <textarea
                            value={formData.reason}
                            onChange={handleReasonChange}
                            rows={3}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            placeholder="Ej: Cierre por vacaciones navideñas, mantenimiento de instalaciones..."
                            maxLength={500}
                        />
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            {formData.reason.length}/500 caracteres
                        </p>
                    </div>

                    {/* Selección de empleados */}
                    <div>
                        <div className="flex items-center justify-between mb-4">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Empleados ({selectedEmployees.length} seleccionados)
                            </label>
                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={selectAll}
                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                    Seleccionar todos
                                </span>
                            </label>
                        </div>

                        <div className="border border-gray-300 dark:border-gray-600 rounded-lg max-h-64 overflow-y-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                                    <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 w-12">
                                            <input
                                                type="checkbox"
                                                checked={selectAll}
                                                onChange={(e) => handleSelectAll(e.target.checked)}
                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                            />
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300">
                                            Nombre
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300">
                                            DNI
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {allEmployees.map(employee => (
                                        <tr key={employee.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                            <td className="px-4 py-2">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedEmployees.some(e => e.id === employee.id)}
                                                    onChange={() => handleEmployeeToggle(employee.id)}
                                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                />
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                                                {employee.name}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                                                {employee.dni}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Botón de submit */}
                    <div className="flex items-center gap-4">
                        <Button
                            type="submit"
                            disabled={submitting || selectedEmployees.length === 0}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                            {submitting ? 'Creando...' : 'Crear Cierre de Empresa'}
                        </Button>
                    </div>
                </form>

                {/* Mensajes de error/éxito */}
                {error && (
                    <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                        <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                    </div>
                )}

                {success && (
                    <div className="mt-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                        <p className="text-sm text-green-800 dark:text-green-200">{success}</p>
                    </div>
                )}
            </div>

            {/* Resultados */}
            {showResults && results && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                        Resultados del Cierre
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                            <p className="text-sm text-blue-600 dark:text-blue-400 font-semibold">
                                Total Empleados
                            </p>
                            <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                                {results.totalEmployees}
                            </p>
                        </div>
                        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                            <p className="text-sm text-green-600 dark:text-green-400 font-semibold">
                                Asignados Correctamente
                            </p>
                            <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                                {results.successCount}
                            </p>
                        </div>
                        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
                            <p className="text-sm text-red-600 dark:text-red-400 font-semibold">
                                Con Errores
                            </p>
                            <p className="text-2xl font-bold text-red-900 dark:text-red-100">
                                {results.errorCount}
                            </p>
                        </div>
                    </div>

                    {results.errors.length > 0 && (
                        <div>
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
                                Errores Detallados
                            </h3>
                            <div className="border border-red-200 dark:border-red-800 rounded-lg max-h-64 overflow-y-auto">
                                <table className="w-full">
                                    <thead className="bg-red-50 dark:bg-red-900/20 sticky top-0">
                                        <tr>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-red-700 dark:text-red-300">
                                                Empleado
                                            </th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-red-700 dark:text-red-300">
                                                Error
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-red-100 dark:divide-red-900">
                                        {results.errors.map((error, idx) => (
                                            <tr key={idx} className="hover:bg-red-50 dark:hover:bg-red-900/10">
                                                <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                                                    {error.employeeName}
                                                </td>
                                                <td className="px-4 py-2 text-sm text-red-600 dark:text-red-400">
                                                    {error.error}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Nota informativa */}
            <div className="mt-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-yellow-800 dark:text-yellow-200 mb-2">
                    Información Importante
                </h3>
                <ul className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
                    <li>• Los cierres de empresa se crean como ausencias de tipo "Cierre de Empresa"</li>
                    <li>• Estas ausencias se marcan como aprobadas automáticamente</li>
                    <li>• Los días se descuentan del balance de vacaciones de cada empleado</li>
                    <li>• Si un empleado ya tiene una ausencia en ese período, no se le asignará el cierre</li>
                    <li>• Solo se asignará a empleados activos en la empresa</li>
                </ul>
            </div>
        </div>
    );
};

export default CierreEmpresaPage;
